'use client';

import { useState, useEffect } from 'react';
import { commentaryService } from '@/lib/services/commentaryService';
import { supabaseBibleRepository } from '@/lib/repositories/supabaseBibleRepository';
import PropTypes from 'prop-types';

export default function VerseDrawer({ bookId, chapter, verse, onClose }) {
  const [verseData, setVerseData] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('commentary');

  useEffect(() => {
    loadVerseDetails();
  }, [bookId, chapter, verse]);

  const loadVerseDetails = async () => {
    setLoading(true);
    try {
      // Get book info first
      const book = await supabaseBibleRepository?.getBookByCode('KJV_EN', bookId?.toUpperCase());
      
      if (!book) {
        console.error('Book not found');
        setLoading(false);
        return;
      }

      // Get chapter info
      const chapterData = await supabaseBibleRepository?.getChapter(book?.id, chapter);
      
      if (!chapterData) {
        console.error('Chapter not found');
        setLoading(false);
        return;
      }

      // Get verse info
      const verseInfo = await supabaseBibleRepository?.getVerseByNumber(chapterData?.id, verse);
      
      if (!verseInfo) {
        console.error('Verse not found');
        setLoading(false);
        return;
      }

      setVerseData({
        book: book?.bookName,
        bookCode: book?.bookCode,
        chapter,
        verse,
        text: verseInfo?.text,
        verseId: verseInfo?.id
      });

      // Load commentary using the verse ID
      if (verseInfo?.id) {
        const commentaryData = await commentaryService?.getCommentaryByVerseId(verseInfo?.id);
        setCommentary(commentaryData);
      }
    } catch (error) {
      console.error('Error loading verse details:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-card border-l border-border shadow-xl z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
        <h3 className="font-semibold text-foreground">
          {verseData ? `${verseData?.book} ${verseData?.chapter}:${verseData?.verse}` : 'Loading...'}
        </h3>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-accent transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Verse Text */}
            {verseData && (
              <div className="mb-6">
                <p className="text-sm leading-relaxed text-foreground font-source-serif">
                  {verseData?.text}
                </p>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-border mb-4">
              <button
                onClick={() => setActiveTab('commentary')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'commentary' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Commentary
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'notes' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                My Notes
              </button>
            </div>

            {/* Commentary Tab */}
            {activeTab === 'commentary' && (
              <div className="space-y-4">
                {commentary ? (
                  <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <span className="text-sm font-semibold text-accent">
                        {commentary?.type === 'verse' ? 'Verse' : 
                         commentary?.type === 'chapter' ? 'Chapter' : 'Book'} Commentary
                      </span>
                    </div>
                    <div className="text-sm leading-relaxed text-foreground font-source-serif">
                      {commentary?.commentary?.content?.split('\n')?.map((para, idx) => (
                        <p key={idx} className="mb-3 last:mb-0">
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-muted-foreground mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <p className="text-sm text-muted-foreground mb-2">
                      No commentary available for this verse yet.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Add commentary from the{' '}
                      <a 
                        href="/commentary-management-admin" 
                        className="text-primary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Admin Panel
                      </a>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground text-center py-8">
                  Personal notes feature coming soon
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

VerseDrawer.propTypes = {
  bookId: PropTypes?.string?.isRequired,
  chapter: PropTypes?.number?.isRequired,
  verse: PropTypes?.number?.isRequired,
  onClose: PropTypes?.func?.isRequired
};